import { AppType } from "client/Api/Apps/common";
import { TileRenderState } from "../../RenderState/common";
import { ReactElement, useCallback } from "react";
import { TileContextProvider } from "../../TileContextProvider";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "../useInvokeNavigation";
import { navigateToIntegrationApp } from "client_legacy/Apps/appNavigation";
import { useRenderManager } from "../../useRenderManager";
import { IntegrationLiveTileContent } from "./IntegrationLiveTileContent";
import { TimeZone } from "client/DateAndTime/timezones";
import { Coordinate } from "client/Misc/commonTypes";

type IntegrationTileContentProps = {
	renderState: TileRenderState<AppType.Integration>;
	userTimezone: TimeZone;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const IntegrationTileContent = (props: IntegrationTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu,
		userTimezone
	} = props;

	const {
		dimension,
		data: { settings }
	} = renderState;

	const [navigationElement, navigateHandler] = useIntegrationTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<IntegrationLiveTileContent
				settings={settings}
				onNavigation={navigateHandler}
				userTimezone={userTimezone}
				onOpenTileMenu={onOpenTileMenu}
			/>
		)
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
IntegrationTileContent.displayName = "IntegrationTileContent";

function useIntegrationTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Integration>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const {
		conditionalAccess,
		maintenanceWindow
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					() => {
						const {
							navigationUrl,
							destinationWindow
						} = renderStateIsh
							.data
							.settings;

						if (!navigationUrl) {
							throw new Error(`Navigate is invoked for an Integration tile without navigation URL with id ${renderStateIsh.data.id}`);
						}

						navigateToIntegrationApp(
							navigationUrl,
							destinationWindow
						);
					},
					[renderStateIsh]
				)
			)
		)
	);
}
